/* eslint-disable react-hooks/exhaustive-deps */
import { ReactNode, useMemo } from 'react';

import { Divider } from '@/components';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useGetPlayerPositionByAbbreviation } from '@/hooks/usePlayer';
import { useTeamStore } from '@/hooks/useTeamStore';
import { KeysOf, Optional } from '@/types';
import {
  EnumTeamContext,
  PlayerComponentInField,
  PlayerDataComponentInField,
  TacticOnField,
} from '@/types/team';
import { formatMillionBalance } from '@/utils';
import {
  PLAYER_FIELD_POSITION_BY_ABBREVIATION,
  SUBTITUTES_ID_KEYS_BY_ABBREVIATION,
} from '@/utils/constants';

type Props = {
  actions?: ReactNode;
  context: EnumTeamContext;
};

type PositionKey = Optional<
  KeysOf<typeof PLAYER_FIELD_POSITION_BY_ABBREVIATION>
>;

const MAX_PLAYERS_F5_POSITIONS_QUANTITY = 5;
const MAX_PLAYERS_F11_POSITIONS_QUANTITY = 15;
const MAX_GOALKEAPERS_F11_POSITIONS_QUANTITY = 2;

export const MoneyActionsDisplayer = ({ actions, context }: Props) => {
  const { isMobile, isDesktop } = useIsDesktop();
  const { state } = useTeamStore();
  const { playersOnField, playersOnFieldF5 } = state;
  const isBuildF5 = context === EnumTeamContext.Build5;

  const selectedPositionLabel = useGetPlayerPositionByAbbreviation(
    state.selectedPosition
  );

  // Function para obtener el numero de jugadores seleccionados POR POSICION
  const selectedPositionLength = useMemo(() => {
    const positionKeyId = state.selectedPosition as PositionKey;
    if (positionKeyId && playersOnField && isMobile) {
      const playersPositionKey =
        PLAYER_FIELD_POSITION_BY_ABBREVIATION[positionKeyId];

      const positionsList = playersOnField[playersPositionKey];

      if (Array.isArray(positionsList)) {
        return positionsList.length + 1;
      } else {
        return MAX_GOALKEAPERS_F11_POSITIONS_QUANTITY; // Esto solo se ejecuta si selecciona el portero
      }
    }
    return MAX_PLAYERS_F11_POSITIONS_QUANTITY;
  }, [playersOnField, state.selectedPosition, isMobile]);

  // Function para obtener el numero de jugadores seleccionados en desktop
  const calculateSelectedPlayersOnDesktop = useMemo(() => {
    let positionsArr: JSX.Element[] = [];

    if (playersOnField) {
      for (const key in playersOnField) {
        const parsedKey = key as keyof TacticOnField;
        const objEl = playersOnField[parsedKey];

        if (objEl) {
          if (Array.isArray(objEl)) {
            positionsArr = [...positionsArr, ...objEl];
          } else {
            positionsArr = [...positionsArr, objEl];
          }
        }
      }
    }

    return positionsArr.filter(
      (el: PlayerComponentInField) => el.props?.data?.id
    ).length;
  }, [playersOnField, isDesktop]);

  // Function para obtener el numero de jugadores seleccionados en mobile
  const calculateSelectedPlayersOnMobile = useMemo(() => {
    let positionsArr: number[] = [];

    const positionKeyId = state.selectedPosition as PositionKey;

    if (positionKeyId && playersOnField) {
      const positionAbbreviationKey =
        PLAYER_FIELD_POSITION_BY_ABBREVIATION[positionKeyId];

      const position = playersOnField[positionAbbreviationKey];

      // Get only elements who has data prop
      if (position) {
        if (Array.isArray(position)) {
          position.forEach((playerComponent: PlayerComponentInField) => {
            const playerId = playerComponent.props?.data?.id;

            if (playerId) {
              positionsArr = [...positionsArr, playerId];
            }
          });
        } else {
          const playerId = position.props?.data?.id;

          if (playerId) {
            positionsArr = [...positionsArr, playerId];
          }
        }
      }

      // Calculate and add substitutes to array by positionKeyId
      const { substitutes } = playersOnField;

      substitutes.forEach((playerPosition, index) => {
        const positionKey =
          positionKeyId as keyof typeof SUBTITUTES_ID_KEYS_BY_ABBREVIATION;
        const selectedIndex = SUBTITUTES_ID_KEYS_BY_ABBREVIATION[positionKey];

        if (selectedIndex === index) {
          const playerProps =
            playerPosition.props as PlayerDataComponentInField;
          const havePlayer = playerProps?.data?.id;

          if (havePlayer) {
            positionsArr = [...positionsArr, havePlayer];
          }
        }
      });
    }

    return positionsArr.length;
  }, [playersOnField, state.selectedPosition, isMobile]);

  const selectedPlayersLength = useMemo(() => {
    if (isDesktop) {
      return calculateSelectedPlayersOnDesktop;
    }

    return calculateSelectedPlayersOnMobile;
  }, [
    isDesktop,
    calculateSelectedPlayersOnDesktop,
    calculateSelectedPlayersOnMobile,
  ]);

  const selectedPlayersQuantityF5 = useMemo(() => {
    let fieldPlayers = 0;
    if (playersOnFieldF5) {
      fieldPlayers = playersOnFieldF5?.fieldPlayers.filter(
        (player) => player.props.data.id
      ).length;
    }
    const goalkeeper = playersOnFieldF5?.goalkeeper?.props.data.id ? 1 : 0;
    return fieldPlayers + goalkeeper;
  }, [isDesktop, state.selectedPosition, playersOnFieldF5]);

  const getPlayersOnFieldCounter: () => string = () => {
    if (isBuildF5) {
      return `${selectedPlayersQuantityF5}/${MAX_PLAYERS_F5_POSITIONS_QUANTITY}`;
    }

    // Por defecto F11.
    return `${selectedPlayersLength}/${selectedPositionLength}`;
  };

  return (
    <header className="bg-white flex justify-between w-full px-5 py-3">
      <div className="flex items-start gap-4">
        <div>
          <p className="text-3xs uppercase text-gdt-gray-4 font-medium">
            {context !== EnumTeamContext.Build5 ? 'Disponible' : 'Presupuesto'}
          </p>
          <p className="text-base font-semibold">
            {!isBuildF5
              ? state.balance
                ? `$${formatMillionBalance(state.balance)}`
                : '$0'
              : 'ilimitado'}
          </p>
        </div>
        <Divider className="h-[35px]" type="vertical" />
        <div>
          <p className="text-3xs uppercase text-gdt-gray-4 font-medium">
            {isBuildF5 ? 'JUGADORES' : selectedPositionLabel}
          </p>
          <p className="text-base font-semibold">
            {getPlayersOnFieldCounter()}
          </p>
        </div>
      </div>
      <div>{actions}</div>
    </header>
  );
};
